<template>
  <div class="AccountForms">
    <div class="panels">
      <AddressPanel
          class="address-panel"
          :address="account.shippingAddress"
          :editable="isPanelEditable('shippingAddress')"
          :title="`Adres dostawy`"
          :isLoading="isLoadingAccount"
          @edit="$emit('editAddress', 'shippingAddress')"
          @save="$emit('saveChanges','shippingAddress')"
      />
      <div class="invoice-question font-size-middle">
        <span class="title">Potrzebujesz faktury do zamówienia?</span>
        <span class="description">
          Jeśli podczas zakupu nie uzupełnisz danych do faktury,
          to dokument sprzedaży zostanie wystawiony na Dane odbiorcy przesyłki.
        </span>
        <div class="want-invoice-container">
          <label class="lyric-checkbox-wrapper">
            <input
              type="checkbox"
              v-model="showBillingAddress"
              class="lyric-checkbox"
              @click="toggleShowBillingAddress()">
            <span class="checkbox-styled">
              <font-awesome-icon class="check" icon="check"/>
            </span>
            <span class="want-invoice-description">Zaznacz jeżeli chcesz fakturę</span>
          </label>
        </div>
      </div>
      <InvoiceAddressPanel
          v-if="showBillingAddress"
          class="address-panel invoice-billing-address"
          :address="account.billingAddress"
          :editable="isPanelEditable('billingAddress')"
          :title="`Dane do faktury`"
          :isLoading="isLoadingInvoice"
          @edit="$emit('editAddress', 'billingAddress')"
          @save="$emit('saveChanges', 'billingAddress')"
      />
      <PasswordPanel
          class="password-panel"
          :class="{'password-panel-open':passwordEditable===true}"
          :editable="passwordEditable"
          :isLoading="isLoadingPassword"
          @edit="$emit('editPassword')"
          @save="$emit('savePassword', $event)"
      />
      <DeleteAccountPanel
          v-if="isAllowedToDelete"
          :account="account"
          @delete="$emit('delete')"
      />
    </div>
  </div>
</template>

<script>
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import { library } from '@fortawesome/fontawesome-svg-core'
import AddressPanel from './AddressPanel.vue'
import PasswordPanel from './PasswordPanel.vue'
import DeleteAccountPanel from './DeleteAccountPanel.vue'
import { getAccountService } from '../../services/accountService'
import { getAuthService } from '../../services/authService'
import { ADMIN } from '../../types/role.types'
import InvoiceAddressPanel from './InvoiceAddressPanel.vue'

library.add(faTrash)

export default {
  name: 'AccountForms',
  components: {
    AddressPanel,
    PasswordPanel,
    DeleteAccountPanel,
    InvoiceAddressPanel,
  },
  props: {
    account: {
      required: true,
    },
    addressesEditModes: {
      required: true,
    },
    passwordEditable: {
      type: Boolean,
      required: true,
    },
    isLoadingAccount: {
      type: Boolean,
      default: false
    },
    isLoadingInvoice: {
      type: Boolean,
      default: false
    },
    isLoadingPassword: {
      type: Boolean,
      default: false
    },
    isShippingEmpty: {
      type: Boolean,
      required: false,
      default: false,
    },
    isInvoiceEmpty: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  data () {
    return {
      confirmDeletePopup: false,
      userRole: null,
      showBillingAddress: this.account.billingAddress.invoiceWanted,
      copyInvoice: null,
    }
  },
  computed: {
    isAllowedToDelete () {
      return this.userRole === ADMIN
    },
  },
  methods: {
    isPanelEditable (index) {
      if (index === 'billingAddress' && this.isInvoiceEmpty) {
        return !!this.addressesEditModes[index]
      } else if (index === 'shippingAddress' && this.isShippingEmpty) {
        return !!this.addressesEditModes[index]
      }
      return !!this.addressesEditModes[index]
    },
    toggleShowBillingAddress () {
      this.toggleInvoice()
      this.$emit('invoiceTick', this.showBillingAddress)
    },
    isShippingAddressEmpty () {
      return this.account.shippingAddress.city.length === 0
    },
    isInvoiceAddressEmpty () {
      return this.account.billingAddress.city.length === 0
    },
    async toggleInvoice () {
      this.copyInvoice.billingAddress.invoiceWanted = !this.copyInvoice.billingAddress.invoiceWanted

      if (this.isInvoiceAddressEmpty() && this.addressesEditModes['billingAddress'] === false) {
        this.addressesEditModes["billingAddress"] = !this.addressesEditModes["billingAddress"]
      }
    }
  },
  async mounted () {
    this.userRole = await getAuthService()
        .getRole()

    this.copyInvoice = JSON.parse(JSON.stringify(this.account))
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";
@import "@/assets/scss/globals.scss";

.AccountForms {
  box-sizing: border-box;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  margin-top: 16px;

  .panels {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    grid-gap: 50px 200px;
    margin: 0 auto;

    .invoice-question {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: auto 20px auto 20px 1fr;
      grid-column: 1;

      .title {
        grid-row-start: 1;
        color: darken(white, 18%);
      }

      .description {
        grid-row-start: 3;
        color: grey;
        letter-spacing: -0.1px;
      }

      .want-invoice-container {
        grid-row-start: 5;
        display: grid;
        grid-template-columns: auto 20px 1fr;

        .want-invoice-description {
          grid-column-start: 3;
          color: darken(white, 15%)
        }
      }
    }

    .password-panel{
      grid-column: 2;
      grid-row: 1;
    }

    .AddressPanel {
      &.billing-address {
        order: 1;
      }
    }

    .invoice-billing-address {
      grid-column: 1;
      order: 1;
    }

    .addresses {
      display: flex;

      .address-panel {
        width: 300px;
      }
    }
  }
}

@media only screen and (max-width: $phone-max-width) {
  .AccountForms {
    max-width: 500px;

    .panels {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: auto auto;
      grid-gap: 50px 200px;

      .password-panel{
        grid-column: 1;
        height: 35px;
      }

      .password-panel-open{
        height: auto;
      }

      .AddressPanel {
        &.billing-address {
          order: initial;
        }
      }

      .invoice-billing-address {
        order: initial;
      }
    }
  }
}

@media screen and (max-width: 1280px) and (-webkit-min-device-pixel-ratio: 1.1) {

  .AccountForms {
    max-width: 500px;

    .panels {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: auto auto;
      grid-gap: 50px 200px;

      .password-panel{
        grid-column: 1;
        height: 35px;
      }

      .password-panel-open{
        height: auto;
      }

      .AddressPanel {
        &.billing-address {
          order: initial;
        }
      }

      .invoice-billing-address {
        order: initial;
      }
    }
  }
}

.lyric-checkbox-wrapper {
    .want-invoice-description {
      cursor: pointer;
      margin-left: .5rem;
    }

  .checkbox-styled {
    cursor: pointer;
    user-select: none;
    padding: 1px 2px;
    background-color: #eee;
    border: solid 1px $primary;
    border-radius: 3px;
    color: #eee;
    font-size: 0.7em;
    transition: 0.1s ease-out background-color;

    &.disabled {
        pointer-events: none;
        opacity: 0.2;
    }

    .check {
        opacity: 0;
        transition: 0.2s ease-out opacity;
    }

    &.error {
        background-color: red;
    }
  }

  input[type=checkbox] {
    display:none;
    &:checked {
      & + .checkbox-styled {
        background-color: $primary;

        .check {
            opacity: 1;
        }
      }
    }
  }
}
</style>
